/*
 * TerriSTORY®
 *
 © Copyright 2022 AURA-EE
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * A copy of the GNU Affero General Public License should be present along
 * with this program at the root of current repository. If not, see
 * http://www.gnu.org/licenses/.
 */

import React from "react";
import { Bar } from "react-chartjs-2";
import FadeInSection from "../Utils/FadeInSection";
function Visits(_ref) {
  let {
    regions,
    years,
    data,
    paramKey,
    title,
    unit
  } = _ref;
  const colors = ["#00a0dd", "#f5217a", "#84c753", "#f7ad4c", "#00857d", "#f6424a"];
  const config = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: title,
        position: "bottom",
        padding: {
          bottom: 20
        },
        color: "rgb(33, 37, 41)",
        font: {
          size: 16
        }
      }
    },
    scales: {
      x: {
        ticks: {
          minRotation: 45,
          font: {
            size: 14
          }
        }
      },
      y: {
        ticks: {
          font: {
            size: 14
          }
        },
        display: true,
        title: {
          display: true,
          text: unit,
          color: "rgb(33, 37, 41)",
          font: {
            size: 16
          }
        }
      }
    }
  };
  const getTransparency = (yearId, nb_years) => {
    return parseInt((yearId + 1) / nb_years * 200 + 55, 10).toString(16);
  };
  const datasets = [];
  years.forEach((year, yearId) => {
    datasets.push({
      label: year,
      data: regions.map(r => {
        var _data$r, _data$r$paramKey;
        return (_data$r = data[r]) === null || _data$r === void 0 ? void 0 : (_data$r$paramKey = _data$r[paramKey]) === null || _data$r$paramKey === void 0 ? void 0 : _data$r$paramKey[year];
      }),
      borderColor: regions.map((r, i) => colors[i] + getTransparency(yearId, years.length)),
      backgroundColor: regions.map((r, i) => colors[i] + getTransparency(yearId, years.length))
    });
  });
  const finalData = {
    labels: regions,
    datasets: datasets
  };
  return /*#__PURE__*/React.createElement(Bar, {
    height: 250,
    options: config,
    data: finalData
  });
}
function Indicators(_ref2) {
  let {
    regions,
    data,
    paramKey,
    title,
    unit
  } = _ref2;
  const colors = ["#00a0dd", "#f5217a", "#84c753", "#f7ad4c", "#00857d", "#f6424a"];
  const config = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: title,
        position: "bottom",
        padding: {
          bottom: 20
        },
        color: "rgb(33, 37, 41)",
        font: {
          size: 16
        }
      }
    },
    scales: {
      x: {
        ticks: {
          minRotation: 45,
          font: {
            size: 14
          }
        }
      },
      y: {
        ticks: {
          font: {
            size: 14
          }
        },
        display: true,
        title: {
          display: true,
          text: unit,
          color: "rgb(33, 37, 41)",
          font: {
            size: 16
          }
        }
      }
    }
  };
  const datasets = [];
  datasets.push({
    data: regions.map(r => {
      var _data$r2;
      return (_data$r2 = data[r]) === null || _data$r2 === void 0 ? void 0 : _data$r2[paramKey];
    }),
    borderColor: regions.map((r, i) => colors[i] + "88"),
    backgroundColor: regions.map((r, i) => colors[i] + "88")
  });
  const finalData = {
    labels: regions,
    datasets: datasets
  };
  return /*#__PURE__*/React.createElement(Bar, {
    height: 250,
    options: config,
    data: finalData
  });
}

/**
 * This component is used to manage the 'A propos' page of the application
 */
function Stats() {
  const regionalStats = {
    "Auvergne-Rhône-Alpes": {
      indicators: 89,
      poi: 14,
      unique_users: 5000,
      population: 8235923,
      ratio_pop: 12,
      visits: {
        2022: 123340,
        2023: 185788
      }
    },
    Bretagne: {
      indicators: 38,
      poi: 8,
      unique_users: 1245,
      population: 3453023,
      ratio_pop: 5.1,
      visits: {
        2022: 34776,
        2023: 41718
      }
    },
    Corse: {
      indicators: 35,
      unique_users: 140,
      population: 355528,
      ratio_pop: 0.5,
      visits: {
        2023: 3868
      }
    },
    "Nouvelle-Aquitaine": {
      indicators: 34,
      unique_users: 1400,
      population: 6154772,
      ratio_pop: 9,
      visits: {
        2022: 37079,
        2023: 40623
      }
    },
    Occitanie: {
      indicators: 35,
      poi: 11,
      unique_users: 2159,
      population: 6154729,
      ratio_pop: 9,
      visits: {
        2022: 45706,
        2023: 91185
      }
    },
    "Pays de la Loire": {
      indicators: 46,
      poi: 23,
      unique_users: 1600,
      population: 3926389,
      ratio_pop: 5.7,
      visits: {
        2022: 16410,
        2023: 55172
      }
    }
  };
  const nationalStats = {
    indicators: 0,
    poi: 0,
    unique_users: 0,
    population: 0,
    ratio_pop: 0,
    visits: {
      2022: 0,
      2023: 0
    }
  };
  Object.values(regionalStats).forEach(value => {
    var _value$indicators, _value$poi, _value$unique_users, _value$population, _value$ratio_pop;
    nationalStats.indicators += (_value$indicators = value.indicators) !== null && _value$indicators !== void 0 ? _value$indicators : 0.0;
    nationalStats.poi += (_value$poi = value.poi) !== null && _value$poi !== void 0 ? _value$poi : 0.0;
    nationalStats.unique_users += (_value$unique_users = value.unique_users) !== null && _value$unique_users !== void 0 ? _value$unique_users : 0.0;
    for (const key in nationalStats.visits) {
      var _value$visits$key, _value$visits;
      nationalStats.visits[key] += (_value$visits$key = (_value$visits = value.visits) === null || _value$visits === void 0 ? void 0 : _value$visits[key]) !== null && _value$visits$key !== void 0 ? _value$visits$key : 0.0;
    }
    nationalStats.population += (_value$population = value.population) !== null && _value$population !== void 0 ? _value$population : 0.0;
    nationalStats.ratio_pop += (_value$ratio_pop = value.ratio_pop) !== null && _value$ratio_pop !== void 0 ? _value$ratio_pop : 0.0;
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "widgets full-screen-widget"
  }, /*#__PURE__*/React.createElement("div", {
    className: "tout about"
  }, /*#__PURE__*/React.createElement("div", {
    className: "texte-corps"
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-block"
  }, /*#__PURE__*/React.createElement("div", {
    className: "whole-width"
  }, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement("h1", null, "Le projet en quelques chiffres"))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement("p", {
    className: "digit color1"
  }, new Intl.NumberFormat("fr-FR").format(nationalStats.unique_users)), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, "utilisateurs uniques"), " sur l'ann\xE9e qui visitent l'ensemble des diff\xE9rents sites r\xE9gionaux."))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement("p", {
    className: "digit color2"
  }, nationalStats.indicators), /*#__PURE__*/React.createElement("p", null, "indicateurs sur l'ensemble des r\xE9gions"), /*#__PURE__*/React.createElement("p", {
    className: "digit color2"
  }, "15"), /*#__PURE__*/React.createElement("p", null, "th\xE9matiques abord\xE9es dont la", " ", /*#__PURE__*/React.createElement("em", null, "consommation d'\xE9nergie"), ", les", " ", /*#__PURE__*/React.createElement("em", null, "puits de carbone"), " ou encore l'", /*#__PURE__*/React.createElement("em", null, "eau")), /*#__PURE__*/React.createElement("p", {
    className: "digit-s color2"
  }, nationalStats.poi), /*#__PURE__*/React.createElement("p", null, "couches d'\xE9quipements disponibles")))), /*#__PURE__*/React.createElement("div", {
    className: "text-block"
  }, /*#__PURE__*/React.createElement("div", {
    className: "whole-width"
  }, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement("h1", null, "Les r\xE9gions \xE0 la manoeuvre"))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement("p", {
    className: "digit color3"
  }, "6"), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, "r\xE9gions"), " accessibles et engag\xE9es dans le consortium TerriSTORY\xAE"))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement("p", {
    className: "digit-xs color3"
  }, new Intl.NumberFormat("fr-FR").format(Math.round(nationalStats.population / 1000, 0) * 1000)), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, "habitants couverts"), " par les d\xE9clinaisons r\xE9gionales sur toute la France, soit"), /*#__PURE__*/React.createElement("p", {
    className: "digit-s color1"
  }, Math.round(nationalStats.ratio_pop, 0), " %"), /*#__PURE__*/React.createElement("p", null, "de la population fran\xE7aise."))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement(Visits, {
    regions: Object.keys(regionalStats),
    years: [2022, 2023],
    data: regionalStats,
    paramKey: "visits",
    title: "Nombre de visites sur les derni\xE8res ann\xE9es par r\xE9gion",
    unit: "Nombre de visites"
  }))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", null, "La dynamique de TerriSTORY\xAE se", " ", /*#__PURE__*/React.createElement("strong", null, "renforce ann\xE9e apr\xE8s ann\xE9e"), " gr\xE2ce au soutien de nos utilisateurs. De nouvelles r\xE9gions ont", " ", /*#__PURE__*/React.createElement("strong", null, "rejoint progressivement le consortium"), " ", "pour d\xE9ployer leur plateforme r\xE9gionale et accompagner toujours plus les territoires dans leurs d\xE9marches de transition environnementale.")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", null, "Chaque structure en charge du d\xE9ploiement r\xE9gional d\xE9cide des ", /*#__PURE__*/React.createElement("strong", null, "indicateurs"), " qu'elle souhaite inclure et ", /*#__PURE__*/React.createElement("strong", null, "mettre \xE0 disposition"), " dans sa plateforme TerriSTORY\xAE.")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement(Indicators, {
    regions: Object.keys(regionalStats),
    data: regionalStats,
    paramKey: "indicators",
    title: "Nombre d'indicateurs par r\xE9gion",
    unit: "Indicateurs"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "text-block"
  }, /*#__PURE__*/React.createElement("div", {
    className: "whole-width"
  }, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement("h1", null, "Un outil informatique en d\xE9veloppement"))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement("p", {
    className: "digit-xs color5"
  }, "2 500"), /*#__PURE__*/React.createElement("p", null, "propositions d'am\xE9lioration ou retours de bugs faites sur le", " ", /*#__PURE__*/React.createElement("a", {
    href: "https://gitlab.com/terristory/terristory/-/issues",
    target: "_blank",
    rel: "noopener noreferrer"
  }, "d\xE9p\xF4t public du code"), " ", "dont"), /*#__PURE__*/React.createElement("p", {
    className: "digit-xs color5"
  }, "2 100"), /*#__PURE__*/React.createElement("p", null, "demandes trait\xE9es (voir la", " ", /*#__PURE__*/React.createElement("a", {
    href: "/national/open_source",
    target: "_blank",
    rel: "noopener noreferrer"
  }, "page open-source pour plus d'informations"), ")."))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FadeInSection, null, /*#__PURE__*/React.createElement("p", {
    className: "digit-xs color6"
  }, "250 000"), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, "lignes de code source"), " \xE0 l'oeuvre derri\xE8re TerriSTORY\xAE dont 86 000 de ", /*#__PURE__*/React.createElement("em", null, "Python"), " ", "et 88 000 de ", /*#__PURE__*/React.createElement("em", null, "JavaScript"), ".")))))));
}
export default Stats;